import type { SchoolRecord } from '@shared/schools/schoolData.js';
import type { SchoolHeritage } from '@shared/schools/schoolHeritage.js';
import type { SchoolRichHeritage } from '@shared/schools/schoolRichHeritage.js';

const SCHOOL_DATA_BASE_PATH = '/school-data';

export const PRIMARY_SCHOOL_DATA_PATH = `${SCHOOL_DATA_BASE_PATH}/primary.json`;
const SECONDARY_SCHOOL_DATA_PATH = `${SCHOOL_DATA_BASE_PATH}/secondary.json`;

const requestCache = new Map<string, Promise<unknown>>();

export class SchoolDataRequestError extends Error {
  readonly path: string;
  readonly status: number;

  constructor(path: string, status: number) {
    super(`Failed to load ${path}: ${status}`);
    this.name = 'SchoolDataRequestError';
    this.path = path;
    this.status = status;
  }
}

async function loadJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new SchoolDataRequestError(path, response.status);
  }

  // The server's SPA fallback may return 200 with HTML for missing static JSON
  // files. Treat this as a 404 so callers can handle it gracefully.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new SchoolDataRequestError(path, 404);
  }

  return response.json() as Promise<T>;
}

function loadCachedJson<T>(path: string): Promise<T> {
  const cachedRequest = requestCache.get(path);
  if (cachedRequest) {
    return cachedRequest as Promise<T>;
  }

  const request = loadJson<T>(path).catch(error => {
    requestCache.delete(path);
    throw error;
  });

  requestCache.set(path, request);
  return request;
}

export function loadPrimarySchools(): Promise<readonly SchoolRecord[]> {
  return loadCachedJson<readonly SchoolRecord[]>(PRIMARY_SCHOOL_DATA_PATH);
}

export function loadSecondarySchools(): Promise<readonly SchoolRecord[]> {
  return loadCachedJson<readonly SchoolRecord[]>(SECONDARY_SCHOOL_DATA_PATH);
}

export function loadSchoolBySlug(slug: string): Promise<SchoolRecord> {
  const normalizedSlug = String(slug).trim().toLowerCase();
  const encodedSlug = encodeURIComponent(normalizedSlug);
  return loadCachedJson<SchoolRecord>(
    `${SCHOOL_DATA_BASE_PATH}/by-slug/${encodedSlug}.json`
  );
}

export async function loadSchoolHeritage(
  slug: string
): Promise<SchoolHeritage | null> {
  const normalizedSlug = String(slug).trim().toLowerCase();
  const encodedSlug = encodeURIComponent(normalizedSlug);
  const path = `${SCHOOL_DATA_BASE_PATH}/heritage-meta/${encodedSlug}.json`;
  try {
    return await loadCachedJson<SchoolHeritage>(path);
  } catch (error) {
    if (error instanceof SchoolDataRequestError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function loadSchoolRichHeritage(
  slug: string
): Promise<SchoolRichHeritage | null> {
  const normalizedSlug = String(slug).trim().toLowerCase();
  const encodedSlug = encodeURIComponent(normalizedSlug);
  const path = `${SCHOOL_DATA_BASE_PATH}/heritage/${encodedSlug}.json`;
  try {
    return await loadCachedJson<SchoolRichHeritage>(path);
  } catch (error) {
    if (error instanceof SchoolDataRequestError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function resetSchoolDataCacheForTests() {
  requestCache.clear();
}
